/**
 * BOSS Reports — the pre-filter URL codec.
 *
 * Every pre-filter a user picks lives in the query string, so a report link reopens the exact
 * window, store set and value list somebody else was looking at. The grammar is fixed:
 *
 *   f_<FIELDNAME>=Vancouver,Burnaby,%21Warehouse   include ["Vancouver","Burnaby"], exclude ["Warehouse"]
 *   d_<FIELDNAME>=<dateType>:<a>:<b>:<reo>         e.g. season:SS:2026:1
 *
 * Two rules make the whole thing work:
 *
 * 1. Encoding is lossless. `decodePreFilters(encodePreFilters(v), fields)` deep-equals `v` for
 *    every value the dialog can produce, so the URL is the single source of truth and the run
 *    cache key can simply be the serialised query string.
 * 2. Decoding is total. A hand-edited, truncated or stale URL never throws and never invents a
 *    filter: an unparseable descriptor, an unknown date type, an unknown field name or a
 *    non-numeric prior-years count all yield *no value* for that field.
 *
 * The date semantics mirrored here are the ones the server resolves (contract §5): every window
 * is [start, end) with both bounds on midnight, seasons run Mar 1 / Aug 1 / Dec 26, the Week tab
 * snaps to Monday while the `lastWeek` preset snaps to Sunday, and Custom includes the last day
 * the user picked. They are duplicated client-side for one reason only — so the dialog can show
 * the user the window before they run the report. The server remains the authority.
 */
import type {
    BossDateType,
    BossField,
    BossPreFilterValue,
    BossRelativeRange,
    BossSeasonCode,
} from '../types/boss'

// ── Vocabulary ────────────────────────────────────────────────────────

/** Every date type except "none" — i.e. one tab of the legacy date chooser. */
export type BossDateTab = Exclude<BossDateType, 'none'>

/** The date tabs, in the legacy DateChooserForm order. */
export const DATE_TABS: readonly BossDateTab[] =
    ['day', 'week', 'month', 'season', 'year', 'custom', 'relative'] as const

/** The legacy season codes, exactly as they are stored and displayed. */
export const SEASON_CODES: readonly BossSeasonCode[] = ['SS', 'FW', 'Holiday'] as const

/** The only four relative ranges the legacy data contains. */
export const RELATIVE_RANGES: readonly BossRelativeRange[] =
    ['yesterday', 'lastWeek', 'lastMonth', 'lastYear'] as const

/** The sentinel value meaning "the store I am working in". Exact, case-sensitive. */
export const MY_STORE = 'MYSTORE'

/** How the sentinel is shown in a store list — the legacy "number | name" row shape. */
export const MY_STORE_LABEL = '** | MYSTORE'

/** The legacy message shown when MYSTORE is combined with a real store in one list. */
export const MY_STORE_CONFLICT =
    "MyStore filter can not be used with other stores. If you would like to use other stores as filters, exclude the 'MYSTORE' filter from the list."

/** Which editor a filtered field gets. */
export type BossPreFilterEditor = 'date' | 'store' | 'supplier' | 'value'

const REO_MAX = 5

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
] as const

const RELATIVE_LABELS: Record<BossRelativeRange, string> = {
    yesterday: 'Yesterday',
    lastWeek: 'Last Week',
    lastMonth: 'Last Month',
    lastYear: 'Last Year',
}

// ── Small date helpers (UTC throughout) ───────────────────────────────
// A report window is a wall-clock window. Doing the arithmetic in UTC keeps a daylight-saving
// change from moving a boundary by an hour and silently shifting a sales day.

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Parses `yyyy-MM-dd` strictly; returns null for anything that is not a real calendar date. */
function parseIsoDate(value: string | null | undefined): Date | null {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(5, 7))
    const day = Number(value.slice(8, 10))
    const parsed = new Date(Date.UTC(year, month - 1, day))
    // Rejects 2026-02-30 and friends, which Date.UTC would happily roll forward.
    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
        return null
    }
    return parsed
}

/** True when the string is a real `yyyy-MM-dd` calendar date. */
export function isIsoDate(value: string | null | undefined): boolean {
    return parseIsoDate(value) !== null
}

const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`

const addDays = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000)

const utcDate = (year: number, month: number, day: number) => new Date(Date.UTC(year, month - 1, day))

/**
 * Today's date in the VIEWER's own timezone, as `yyyy-MM-dd`.
 *
 * Not the date a relative window is resolved against. That is the chain's business date, which the
 * server resolves in one configured zone and ships on `BossReportDetail.businessDate`; pass THAT
 * to {@link previewWindow}. This is the last-resort fallback for a caller that has no server date
 * yet, and the value the dialog compares against to tell a viewer their calendar reads differently.
 */
export function todayIso(): string {
    const now = new Date()
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

/**
 * The Monday of the week containing `value`, walking backwards — the legacy Week tab behaviour.
 * Returns null when the input is not a date.
 */
export function mondayOfIso(value: string | null | undefined): string | null {
    const d = parseIsoDate(value)
    if (!d) return null
    // getUTCDay(): 0 = Sunday. Monday-based offset, so Sunday walks back six days, not zero.
    return isoOf(addDays(d, -((d.getUTCDay() + 6) % 7)))
}

/** Clamps a prior-years count into the 0..5 the legacy engine accepted; anything else is 0. */
export function clampReoYears(value: number | null | undefined): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 0
    return Math.min(Math.max(Math.trunc(value), 0), REO_MAX)
}

// ── Field classification ──────────────────────────────────────────────

/**
 * Which editor a filtered field gets.
 *
 * The `value` fallback is deliberate and load-bearing: 330 live legacy fields are filtered with
 * `FilterType = 0`, and the legacy editor reached the string editor for anything that was not a
 * store, a supplier or a date. Treating those as "no filter" would drop a third of every imported
 * report's pre-filters.
 */
/**
 * The editor for a field being filtered AD HOC — one the report never marked filterable, added
 * through the pre-filter dialog's picker for this run only.
 *
 * Such a field usually has no `filterType` at all, so its COLUMN TYPE decides, and a date column
 * gets the window editor rather than the free-text include/exclude list. This mirrors the server's
 * own inference for the same case (BossSqlBuilder.AdHocFilterType) so the editor and the predicate
 * always agree; the legacy tool got exactly this wrong, sending `IN ('...')` against a datetime.
 *
 * A field the report DOES filter is left alone, whatever its column type: its stored `filterType`
 * is what the server will honour, and 330 imported fields are a genuine list filter stored as
 * `none`.
 */
export function adHocEditor(field: BossField): BossPreFilterEditor {
    const base = preFilterEditor(field)
    const undated = !field.isFiltered && field.filterType === 'none'
    if (base === 'value' && undated && (field.dataType === 'date' || field.dataType === 'datetime')) {
        return 'date'
    }
    return base
}

export function preFilterEditor(field: BossField): BossPreFilterEditor {
    if (field.filterType === 'dateAbsolute' || field.filterType === 'dateRelative') return 'date'
    if (field.dateType !== 'none') return 'date'
    if (field.filterType === 'store') return 'store'
    if (field.filterType === 'supplier') return 'supplier'
    return 'value'
}

// ── Value-list escaping ───────────────────────────────────────────────
// A filter value may legitimately contain a comma (a product code like "123,456") or a leading
// "!" — both of which are structural in the joined list — so they are escaped inside each item
// before joining. "%" is escaped first so the escapes themselves round-trip.

const encodeItem = (value: string) =>
    value.replace(/%/g, '%25').replace(/,/g, '%2C').replace(/!/g, '%21')

const decodeItem = (value: string) =>
    value.replace(/%2C/gi, ',').replace(/%21/gi, '!').replace(/%25/gi, '%')

// ── Encoding ──────────────────────────────────────────────────────────

function encodeDateDescriptor(value: BossPreFilterValue): string | null {
    const type = value.dateType
    if (!type || type === 'none') return null
    const reo = String(clampReoYears(value.reoYears))
    const from = value.from
    const to = value.to
    switch (type) {
        case 'day': {
            if (!from || !isIsoDate(from)) return null
            const last = to && isIsoDate(to) ? to : ''
            return `day:${from}:${last}:${reo}`
        }
        case 'week': {
            if (!from || !isIsoDate(from)) return null
            return `week:${from}::${reo}`
        }
        case 'month': {
            if (!from || !isIsoDate(from)) return null
            return `month:${from.slice(0, 7)}::${reo}`
        }
        case 'year': {
            if (!from || !isIsoDate(from)) return null
            return `year:${from.slice(0, 4)}::${reo}`
        }
        case 'season': {
            const code = value.seasonCode
            const year = value.seasonYear
            if (!code || !SEASON_CODES.includes(code)) return null
            if (typeof year !== 'number' || !Number.isInteger(year) || year < 1000 || year > 9999) return null
            return `season:${code}:${year}:${reo}`
        }
        case 'custom': {
            if (!from || !to || !isIsoDate(from) || !isIsoDate(to)) return null
            return `custom:${from}:${to}:${reo}`
        }
        case 'relative': {
            const range = value.relativeRange
            if (!range || !RELATIVE_RANGES.includes(range)) return null
            return `relative:${range}::${reo}`
        }
        default:
            return null
    }
}

function encodeValueList(include: readonly string[], exclude: readonly string[]): string {
    const parts: string[] = []
    for (const item of include) {
        const encoded = encodeItem(item)
        if (encoded !== '') parts.push(encoded)
    }
    for (const item of exclude) {
        const encoded = encodeItem(item)
        if (encoded !== '') parts.push(`!${encoded}`)
    }
    return parts.join(',')
}

/**
 * Serialises pre-filter values into the query-string parameters the page owns.
 *
 * An absent filter and an EMPTY one are not the same thing, and the difference is the whole point
 * of the empty key this writes. The server reads a value carrying two empty lists as "the user
 * cleared this filter" and a field with no value at all as "use the report's stored default"
 * (BossSqlBuilder.ResolveLists) — so a cleared filter that contributed no key came back filtered
 * by the default the user had just removed. `f_X=` says "asked, and the answer was nothing".
 *
 * A value whose lists are empty AND which carries a resolvable date is a date filter, not a
 * cleared list, and writes only its `d_` key.
 */
export function encodePreFilters(values: BossPreFilterValue[]): URLSearchParams {
    const params = new URLSearchParams()
    for (const value of values) {
        if (!value || typeof value.field !== 'string' || value.field === '') continue
        const descriptor = encodeDateDescriptor(value)
        if (descriptor !== null) params.set(`d_${value.field}`, descriptor)
        const list = encodeValueList(value.include ?? [], value.exclude ?? [])
        if (list !== '') params.set(`f_${value.field}`, list)
        else if (descriptor === null) params.set(`f_${value.field}`, '')
    }
    return params
}

// ── Decoding ──────────────────────────────────────────────────────────

/** Returns the prior-years count, or null when the segment is present but not a number. */
function decodeReo(segment: string): number | null {
    if (segment === '') return 0
    if (!/^-?\d+$/.test(segment)) return null
    return clampReoYears(Number(segment))
}

function decodeDateDescriptor(raw: string): Partial<BossPreFilterValue> | null {
    const segments = raw.split(':')
    if (segments.length !== 4) return null
    const [type, a, b, r] = segments
    const reoYears = decodeReo(r)
    if (reoYears === null) return null
    switch (type) {
        case 'day': {
            if (!isIsoDate(a)) return null
            if (b !== '' && !isIsoDate(b)) return null
            return b === ''
                ? { dateType: 'day', from: a, reoYears }
                : { dateType: 'day', from: a, to: b, reoYears }
        }
        case 'week': {
            if (!isIsoDate(a)) return null
            return { dateType: 'week', from: a, reoYears }
        }
        case 'month': {
            if (!/^\d{4}-\d{2}$/.test(a) || !isIsoDate(`${a}-01`)) return null
            return { dateType: 'month', from: `${a}-01`, reoYears }
        }
        case 'year': {
            if (!/^\d{4}$/.test(a)) return null
            return { dateType: 'year', from: `${a}-01-01`, reoYears }
        }
        case 'season': {
            const code = SEASON_CODES.find(c => c === a)
            if (!code || !/^\d{4}$/.test(b)) return null
            return { dateType: 'season', seasonCode: code, seasonYear: Number(b), reoYears }
        }
        case 'custom': {
            if (!isIsoDate(a) || !isIsoDate(b)) return null
            return { dateType: 'custom', from: a, to: b, reoYears }
        }
        case 'relative': {
            const range = RELATIVE_RANGES.find(x => x === a)
            if (!range) return null
            return { dateType: 'relative', relativeRange: range, reoYears }
        }
        default:
            // An unknown date type is not a silent "everything" — it is simply no filter.
            return null
    }
}

function decodeValueList(raw: string): Partial<BossPreFilterValue> | null {
    const include: string[] = []
    const exclude: string[] = []
    for (const segment of raw.split(',')) {
        if (segment === '') continue
        if (segment.startsWith('!')) {
            const item = decodeItem(segment.slice(1))
            if (item !== '') exclude.push(item)
        } else {
            include.push(decodeItem(segment))
        }
    }
    // Never null. The KEY's presence is the answer: `f_X=` is a filter the user cleared, and it has
    // to reach the server as two empty lists so the report's stored default is not put back. A
    // field nobody answered simply has no `f_` key, and never reaches here.
    return { include, exclude }
}

/**
 * Reads pre-filter values back out of the query string.
 *
 * Field names are matched against the report's own fields, so a stale link naming a column the
 * report no longer returns is dropped rather than sent to the server (where it would be a 400).
 * Orphaned fields are dropped for the same reason. Values come back in the order their keys
 * appear in `params`, which is the order `encodePreFilters` wrote them.
 */
export function decodePreFilters(params: URLSearchParams, fields: BossField[]): BossPreFilterValue[] {
    const known = new Map<string, BossField>()
    for (const field of fields) {
        if (!field.isOrphaned) known.set(field.name, field)
    }

    const merged = new Map<string, BossPreFilterValue>()
    for (const [key, raw] of params.entries()) {
        const isDate = key.startsWith('d_')
        const isList = key.startsWith('f_')
        if (!isDate && !isList) continue
        const name = key.slice(2)
        if (!known.has(name)) continue
        const parsed = isDate ? decodeDateDescriptor(raw) : decodeValueList(raw)
        if (parsed === null) continue
        // A field can carry both a d_ and an f_ key; they merge into one value.
        const existing: BossPreFilterValue = merged.get(name) ?? { field: name }
        merged.set(name, { ...existing, ...parsed, field: name })
    }
    return [...merged.values()]
}

// ── Defaults from the stored field definitions ────────────────────────

/** The `yyyy-MM-dd` part of a wire date, or null when it is absent or malformed. */
function datePart(value: string | null | undefined): string | null {
    if (!value) return null
    const day = value.slice(0, 10)
    return isIsoDate(day) ? day : null
}

function dateValueFromField(field: BossField): BossPreFilterValue | null {
    const reoYears = clampReoYears(field.reoYears)
    const from = datePart(field.startDate)
    const to = datePart(field.endDate)
    switch (field.dateType) {
        case 'day':
            if (!from) return null
            return { field: field.name, dateType: 'day', from, to: to ?? from, reoYears }
        case 'week': {
            const monday = mondayOfIso(from)
            if (!monday) return null
            return { field: field.name, dateType: 'week', from: monday, reoYears }
        }
        case 'month':
            if (!from) return null
            return { field: field.name, dateType: 'month', from: `${from.slice(0, 7)}-01`, reoYears }
        case 'year':
            if (!from) return null
            return { field: field.name, dateType: 'year', from: `${from.slice(0, 4)}-01-01`, reoYears }
        case 'season': {
            const code = SEASON_CODES.find(c => c === field.seasonCode)
            if (!code || typeof field.seasonYear !== 'number') return null
            return { field: field.name, dateType: 'season', seasonCode: code, seasonYear: field.seasonYear, reoYears }
        }
        case 'custom':
            // A stored Custom window with only one bound set contributes no predicate at all —
            // the legacy engine dropped both in that case rather than running open-ended.
            if (!from || !to) return null
            return { field: field.name, dateType: 'custom', from, to, reoYears }
        case 'relative': {
            const range = RELATIVE_RANGES.find(r => r === field.relativeRange)
            if (!range) return null
            return { field: field.name, dateType: 'relative', relativeRange: range, reoYears }
        }
        default:
            return null
    }
}

/**
 * The pre-filter values a report starts with when the URL carries none: whatever the designer
 * persisted on each filtered field. A field with nothing persisted contributes no value, which
 * is what leaves a `prompt` field showing up in {@link unresolvedPrompts}.
 */
export function defaultsFromFields(fields: BossField[]): BossPreFilterValue[] {
    const values: BossPreFilterValue[] = []
    for (const field of fields) {
        // Unlike decoding, this reads the *stored* definition, so a field that is not a
        // pre-filter at all must not contribute one.
        if (!field.isFiltered || field.isOrphaned) continue
        if (preFilterEditor(field) === 'date') {
            const value = dateValueFromField(field)
            if (value) values.push(value)
            continue
        }
        const include = field.include ?? []
        const exclude = field.exclude ?? []
        if (include.length === 0 && exclude.length === 0) continue
        values.push({ field: field.name, include: [...include], exclude: [...exclude] })
    }
    return values
}

/**
 * The fields marked "prompt on open" that still have no value — the report must not run until
 * the caller has answered them.
 *
 * PRESENCE is the test, not whether the answer narrows anything. A pre-filter is never mandatory:
 * "prompt" means "ask me", and an empty answer is a legitimate one — it is now representable end
 * to end (`encodePreFilters` writes `f_X=` for it, and the server reads two empty lists as a
 * cleared filter), so a field somebody deliberately emptied must not read back as still unasked
 * and re-open the dialog on every reload.
 */
export function unresolvedPrompts(fields: BossField[], values: BossPreFilterValue[]): BossField[] {
    const answered = new Set(values.map(v => v.field))
    return fields.filter(f => f.prompt && f.isFiltered && !f.isOrphaned && !answered.has(f.name))
}

// ── Description and window preview ────────────────────────────────────

/** The legacy display string for a relative range. */
export function relativeLabel(range: BossRelativeRange): string {
    return RELATIVE_LABELS[range]
}

/** A resolved window, start inclusive and end exclusive, both `yyyy-MM-dd`. */
export type BossWindowPreview = { start: string; end: string }

/**
 * Resolves a date value to the window the server will run, so the dialog can show it before the
 * report goes anywhere. Mirrors the server's resolver (contract §5): every window is
 * [start, end) on midnight boundaries, Custom and Day include the last day picked, the Week tab
 * snaps to Monday while `lastWeek` snaps to Sunday, and an unknown season or range resolves to
 * nothing rather than to all of history.
 *
 * `today` must be the SERVER's business date (`BossReportDetail.businessDate`), which is the date
 * `BossDateResolver` will use. The {@link todayIso} default is a fallback for callers that have no
 * server date; relying on it puts the preview on the viewer's calendar, which is a different day
 * from the run's for any viewer whose evening is the business zone's next morning.
 */
export function previewWindow(value: BossPreFilterValue, today: string = todayIso()): BossWindowPreview | null {
    const now = parseIsoDate(today)
    if (!now) return null

    let start: Date | null = null
    let end: Date | null = null

    switch (value.dateType) {
        case 'day':
        case 'custom': {
            const a = parseIsoDate(value.from)
            // Day tolerates a missing end (a single day); Custom requires both bounds.
            const b = parseIsoDate(value.to) ?? (value.dateType === 'day' ? a : null)
            if (!a || !b) break
            const [lo, hi] = a.getTime() <= b.getTime() ? [a, b] : [b, a]
            start = lo
            end = addDays(hi, 1)
            break
        }
        case 'week': {
            const monday = parseIsoDate(mondayOfIso(value.from))
            if (!monday) break
            start = monday
            end = addDays(monday, 7)
            break
        }
        case 'month': {
            const d = parseIsoDate(value.from)
            if (!d) break
            start = utcDate(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)
            end = utcDate(d.getUTCFullYear(), d.getUTCMonth() + 2, 1)
            break
        }
        case 'year': {
            const d = parseIsoDate(value.from)
            if (!d) break
            start = utcDate(d.getUTCFullYear(), 1, 1)
            end = utcDate(d.getUTCFullYear() + 1, 1, 1)
            break
        }
        case 'season': {
            const year = value.seasonYear
            if (typeof year !== 'number' || !Number.isInteger(year)) break
            switch (value.seasonCode) {
                case 'SS':
                    start = utcDate(year, 3, 1)
                    end = utcDate(year, 8, 1)
                    break
                case 'FW':
                    start = utcDate(year, 8, 1)
                    end = utcDate(year + 1, 3, 1)
                    break
                case 'Holiday':
                    start = utcDate(year, 12, 26)
                    end = utcDate(year + 1, 1, 6)
                    break
                default:
                    break
            }
            break
        }
        case 'relative': {
            switch (value.relativeRange) {
                case 'yesterday':
                    start = addDays(now, -1)
                    end = now
                    break
                case 'lastWeek': {
                    // Sunday-based, unlike the Week tab. Surprising, but it is the shipped behaviour.
                    const thisSunday = addDays(now, -now.getUTCDay())
                    start = addDays(thisSunday, -7)
                    end = thisSunday
                    break
                }
                case 'lastMonth': {
                    const firstOfThis = utcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
                    start = utcDate(now.getUTCFullYear(), now.getUTCMonth(), 1)
                    end = firstOfThis
                    break
                }
                case 'lastYear':
                    start = utcDate(now.getUTCFullYear() - 1, 1, 1)
                    end = utcDate(now.getUTCFullYear(), 1, 1)
                    break
                default:
                    break
            }
            break
        }
        default:
            break
    }

    if (!start || !end || end.getTime() <= start.getTime()) return null
    return { start: isoOf(start), end: isoOf(end) }
}

function describeDate(value: BossPreFilterValue): string {
    const reo = clampReoYears(value.reoYears)
    const suffix = reo > 0 ? `, plus ${reo} prior year(s)` : ''
    switch (value.dateType) {
        case 'day': {
            const a = value.from ?? ''
            const b = value.to ?? a
            if (!isIsoDate(a)) return ''
            return `${a === b ? a : `${a} → ${b}`}${suffix}`
        }
        case 'week': {
            const monday = mondayOfIso(value.from)
            return monday ? `Week of ${monday} (Mon–Sun)${suffix}` : ''
        }
        case 'month': {
            const d = parseIsoDate(value.from)
            return d ? `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}${suffix}` : ''
        }
        case 'year': {
            const d = parseIsoDate(value.from)
            return d ? `${d.getUTCFullYear()}${suffix}` : ''
        }
        case 'season': {
            const code = value.seasonCode
            const year = value.seasonYear
            if (!code || typeof year !== 'number') return ''
            return `Season ${code.toUpperCase()} ${year}${suffix}`
        }
        case 'custom': {
            const a = value.from
            const b = value.to
            if (!a || !b || !isIsoDate(a) || !isIsoDate(b)) return ''
            const [lo, hi] = a <= b ? [a, b] : [b, a]
            return `${lo} → ${hi}${suffix}`
        }
        case 'relative':
            return value.relativeRange ? `${relativeLabel(value.relativeRange)}${suffix}` : ''
        default:
            return ''
    }
}

/** Lists at most four values, then says how many more there are. */
function summariseList(items: readonly string[]): string {
    if (items.length <= 4) return items.join(', ')
    return `${items.slice(0, 4).join(', ')} +${items.length - 4} more`
}

/**
 * A one-line summary of what a value does to a field — the text on the pre-filter chip. Phrased
 * to match the server's `appliedFilters[].description`, so the chip and the run agree.
 */
export function describePreFilter(field: BossField, value: BossPreFilterValue): string {
    const parts: string[] = []
    if (preFilterEditor(field) === 'date' || (value.dateType && value.dateType !== 'none')) {
        const described = describeDate(value)
        if (described !== '') parts.push(described)
    }
    const include = value.include ?? []
    const exclude = value.exclude ?? []
    if (include.length > 0) parts.push(`Include: ${summariseList(include)}`)
    if (exclude.length > 0) parts.push(`Exclude: ${summariseList(exclude)}`)
    return parts.length > 0 ? parts.join('; ') : 'No filter'
}
